/**
 * A picture, as data rather than as pixels.
 *
 * Strokes live in WORLD space, not screen space — the drawing pad pans and
 * zooms across an unbounded plane, so "where the child drew it" cannot mean
 * "where it landed on this particular phone". Everything downstream (saving,
 * reopening on a different-sized screen, exporting, the thumbnail) works off
 * this one format.
 *
 * The wire form is deliberately compact: points as one flat array of numbers
 * rounded to a tenth of a pixel, speeds to two decimals. A busy drawing is
 * ~40% smaller that way, which matters because drafts are held in
 * localStorage, and a tenth of a pixel is far below what a crayon can show.
 */

import type { Stroke } from '../engine/stroke.ts';
import { BRUSHES } from '../engine/stroke.ts';
import type { BrushId } from '../engine/stroke.ts';
import { bounds } from '../engine/geom.ts';

export type SceneKind = 'doodle' | 'mirror';

export interface Scene {
  kind: SceneKind;
  strokes: Stroke[];
  /** Mirror only: how the kaleidoscope was folded when it was drawn. */
  folds?: number;
  flip?: boolean;
}

/** What actually gets written to disk. Numbers only — no nested objects. */
export interface SceneJson {
  v: 1;
  kind: SceneKind;
  strokes: StrokeJson[];
  folds?: number;
  flip?: boolean;
}

interface StrokeJson {
  b: BrushId;
  c: string;
  w: number;
  s: number;
  /** [x0, y0, x1, y1, ...] */
  p: number[];
  /** One per point. */
  v: number[];
  f?: string;
}

const r1 = (n: number): number => Math.round(n * 10) / 10;
const r2 = (n: number): number => Math.round(n * 100) / 100;

export function encodeScene(scene: Scene): SceneJson {
  const out: SceneJson = {
    v: 1,
    kind: scene.kind,
    strokes: scene.strokes.map((s) => {
      const p: number[] = [];
      for (const pt of s.points) {
        p.push(r1(pt.x), r1(pt.y));
      }
      const stroke: StrokeJson = {
        b: s.brush,
        c: s.color,
        w: r2(s.width),
        s: s.seed,
        p,
        v: s.speeds.map(r2),
      };
      if (s.fill) stroke.f = s.fill;
      return stroke;
    }),
  };
  if (scene.folds !== undefined) out.folds = scene.folds;
  if (scene.flip !== undefined) out.flip = scene.flip;
  return out;
}

/**
 * Decode defensively. Anything stored is something that survived a browser
 * upgrade, a quota scare and possibly a half-written record, so a malformed
 * stroke drops that stroke rather than the whole picture — losing one mark is
 * recoverable, losing the drawing is not.
 */
export function decodeScene(raw: unknown): Scene | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const kind = o.kind;
  if (kind !== 'doodle' && kind !== 'mirror') return null;
  if (!Array.isArray(o.strokes)) return null;

  const strokes: Stroke[] = [];
  for (const item of o.strokes) {
    const stroke = decodeStroke(item);
    if (stroke) strokes.push(stroke);
  }

  const scene: Scene = { kind, strokes };
  if (typeof o.folds === 'number' && Number.isFinite(o.folds)) scene.folds = o.folds;
  if (typeof o.flip === 'boolean') scene.flip = o.flip;
  return scene;
}

function decodeStroke(raw: unknown): Stroke | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const brush = o.b;
  if (typeof brush !== 'string' || !(brush in BRUSHES)) return null;
  if (!Array.isArray(o.p) || o.p.length < 2 || o.p.length % 2 !== 0) return null;
  if (typeof o.c !== 'string' || typeof o.w !== 'number' || !Number.isFinite(o.w)) return null;

  const points = [];
  for (let i = 0; i < o.p.length; i += 2) {
    const x = o.p[i];
    const y = o.p[i + 1];
    if (typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) return null;
    points.push({ x, y });
  }

  const speeds: number[] = [];
  const rawSpeeds = Array.isArray(o.v) ? o.v : [];
  for (let i = 0; i < points.length; i++) {
    const s = rawSpeeds[i];
    speeds.push(typeof s === 'number' && Number.isFinite(s) ? s : 0);
  }

  const stroke: Stroke = {
    brush: brush as BrushId,
    color: o.c,
    width: o.w,
    points,
    speeds,
    seed: typeof o.s === 'number' && Number.isFinite(o.s) ? o.s : 0,
  };
  if (typeof o.f === 'string') stroke.fill = o.f;
  return stroke;
}

export interface Box {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * The rectangle every mark of the picture fits inside, padded by the widest
 * brush so a fat crayon edge is not shaved off the thumbnail. Returns null for
 * an empty scene — callers must decide what an empty picture means rather than
 * being handed a degenerate zero box.
 */
export function sceneBounds(scene: Scene): Box | null {
  const points = [];
  let widest = 0;
  for (const s of scene.strokes) {
    if (s.points.length === 0) continue;
    points.push(...s.points);
    widest = Math.max(widest, s.width * BRUSHES[s.brush].scale);
  }
  if (points.length === 0) return null;
  const b = bounds(points);
  const pad = widest * 0.75 + 8;
  return { minX: b.minX - pad, minY: b.minY - pad, maxX: b.maxX + pad, maxY: b.maxY + pad };
}

export interface Camera {
  /** World point at the centre of the viewport. */
  x: number;
  y: number;
  scale: number;
}

export const MIN_ZOOM = 0.2;
export const MAX_ZOOM = 4;

export function clampZoom(scale: number): number {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale));
}

/**
 * The camera that puts the whole picture on screen with a margin. This is what
 * "show me everything" does, and what reopening a saved picture does — a
 * drawing made on a phone must not open off-screen on a laptop.
 */
export function cameraFitting(box: Box | null, width: number, height: number): Camera {
  if (!box || width <= 0 || height <= 0) return { x: 0, y: 0, scale: 1 };
  const w = Math.max(1, box.maxX - box.minX);
  const h = Math.max(1, box.maxY - box.minY);
  const margin = 0.88;
  const scale = clampZoom(Math.min((width / w) * margin, (height / h) * margin));
  return { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2, scale };
}

/** World point under a screen point, for the current camera. */
export function toWorld(cam: Camera, sx: number, sy: number, width: number, height: number) {
  return { x: cam.x + (sx - width / 2) / cam.scale, y: cam.y + (sy - height / 2) / cam.scale };
}

/** Screen point for a world point. */
export function toScreen(cam: Camera, wx: number, wy: number, width: number, height: number) {
  return { x: (wx - cam.x) * cam.scale + width / 2, y: (wy - cam.y) * cam.scale + height / 2 };
}

/** The world rectangle currently visible — what the tile cache needs to fill. */
export function viewBox(cam: Camera, width: number, height: number): Box {
  const halfW = width / 2 / cam.scale;
  const halfH = height / 2 / cam.scale;
  return { minX: cam.x - halfW, minY: cam.y - halfH, maxX: cam.x + halfW, maxY: cam.y + halfH };
}

export function boxesOverlap(a: Box, b: Box): boolean {
  return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}

/** One stroke's own box, padded by its brush width. Cached by the tile layer. */
export function strokeBox(stroke: Stroke): Box {
  const b = bounds(stroke.points);
  const pad = stroke.width * BRUSHES[stroke.brush].scale * 0.75 + 4;
  return { minX: b.minX - pad, minY: b.minY - pad, maxX: b.maxX + pad, maxY: b.maxY + pad };
}
